import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import sanityClient from "../sanityClient";
import LazyLoad from "react-lazyload";
import { Helmet } from "react-helmet";
import CTASection from "../components/CTASection";

const SponsorDetail = () => {
  const { id } = useParams();
  const [sponsor, setSponsor] = useState(null);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "sponsor" && _id == $id]{
          name,
          description,
          website,
          partnershipStartYear,
          aboutThem,
          communityCommitment,
          gratitudeFromHabitat,
          tier,
          "logoUrl": logo.asset->url,
          "galleryImages": gallery[].asset->url,
          sponsoredBuilds[]->{
            title,
            _id,
            description,
            address,
            startDate,
            completionDate,
            family->{
              name,
              biography
            },
            propertyType,
            squareFootage,
            bedrooms,
            bathrooms,
            "mainImageUrl": mainImage.asset->url
          },
          videos[] {
            title,
            description,
            url
          }
        }`,
        { id }
      )
      .then((data) => setSponsor(data[0]))
      .catch(console.error);
  }, [id]);

  if (!sponsor) return <LoadingMessage>Loading...</LoadingMessage>;

  // Calculate the partnership duration dynamically
  const currentYear = new Date().getFullYear();
  const partnershipDuration = sponsor.partnershipStartYear
    ? currentYear - sponsor.partnershipStartYear
    : null;

  // Prepare dynamic opening statement
  const openingStatement = () => {
    let statement = `Habitat for Humanity Buffalo is proud to partner with ${sponsor.name}`;

    // Only add the partnership start year if it exists
    if (sponsor.partnershipStartYear) {
      statement += ` since ${sponsor.partnershipStartYear}`;
    }

    statement += `. `; // Add a period after the sentence

    // Add details about current sponsored builds
    const currentBuilds = sponsor.sponsoredBuilds?.filter(
      (build) => !build.completionDate
    );
    const previousBuilds = sponsor.sponsoredBuilds?.filter(
      (build) => build.completionDate
    );

    if (currentBuilds?.length > 0) {
      if (sponsor.tier === "wholeHouse") {
        statement += `${sponsor.name} is the full sponsor of ${
          currentBuilds.length
        } current ${
          currentBuilds.length === 1 ? "build" : "builds"
        }, including the project at ${currentBuilds
          .map((build) => build.address)
          .join(", ")}. `;
      } else if (sponsor.tier === "halfHouse") {
        statement += `${sponsor.name} is a half-house sponsor for ${
          currentBuilds.length
        } current ${
          currentBuilds.length === 1 ? "build" : "builds"
        }, including the project at ${currentBuilds
          .map((build) => build.address)
          .join(", ")}. `;
      } else {
        statement += `${sponsor.name} is a partial sponsor of ${
          currentBuilds.length === 1 ? "a current build" : "current builds"
        }, including the project at ${currentBuilds
          .map((build) => build.address)
          .join(", ")}. `;
      }
    }

    // Add details about previous sponsored builds
    if (previousBuilds?.length > 0) {
      statement += `Over the years, ${
        sponsor.name
      } has successfully supported ${previousBuilds.length} previous ${
        previousBuilds.length === 1 ? "build" : "builds"
      }, helping to provide safe, affordable housing for families in need. `;
    }

    return statement;
  };

  return (
    <>
      <Helmet>
        <title>{sponsor.name} - Habitat for Humanity Buffalo Sponsor</title>
        <meta name="description" content={sponsor.description} />
      </Helmet>

      {/* Conditional Header with Background Image */}
      {sponsor.galleryImages && sponsor.galleryImages.length > 0 && (
        <HeaderSection>
          <HeaderBackgroundImage
            src={sponsor.galleryImages[0]}
            alt="Sponsor Header Background"
          />
          <HeaderContent>
            <h1>Thank you to {sponsor.name}!</h1>
          </HeaderContent>
        </HeaderSection>
      )}

      <SponsorDetailContainer>
        {/* Display logo or fallback to text */}
        {sponsor.logoUrl ? (
          <LazyLoad height={200} offset={100}>
            <SponsorLogo src={sponsor.logoUrl} alt={sponsor.name} />
          </LazyLoad>
        ) : (
          <SponsorTitle>{sponsor.name}</SponsorTitle>
        )}

        {sponsor.website && (
          <SponsorMeta>
            <a href={sponsor.website} target="_blank" rel="noopener noreferrer">
              Visit Website
            </a>
          </SponsorMeta>
        )}

        {/* Dynamic Opening Statement */}
        <DynamicOpeningStatement>{openingStatement()}</DynamicOpeningStatement>

        {/* About Them Section */}
        {sponsor.aboutThem && (
          <>
            <SectionTitle>About {sponsor.name}</SectionTitle>
            <p>{sponsor.aboutThem}</p>

            {/* Gallery image between sections */}
            {sponsor.galleryImages && sponsor.galleryImages.length > 0 && (
              <LazyLoad height={150} offset={100}>
                <GalleryImage src={sponsor.galleryImages[0]} alt="Gallery" />
              </LazyLoad>
            )}
          </>
        )}

        {/* Community Commitment Section */}
        {sponsor.communityCommitment && (
          <>
            <SectionTitle>
              {sponsor.name}'s Commitment to the Community
            </SectionTitle>
            <p>{sponsor.communityCommitment}</p>

            {/* Gallery image between sections */}
            {sponsor.galleryImages && sponsor.galleryImages.length > 1 && (
              <LazyLoad height={150} offset={100}>
                <GalleryImage src={sponsor.galleryImages[1]} alt="Gallery" />
              </LazyLoad>
            )}
          </>
        )}

        {/* Gratitude Section */}
        {sponsor.gratitudeFromHabitat && (
          <>
            <SectionTitle>
              {sponsor.name} is a Valued Partner of Habitat Buffalo
            </SectionTitle>
            <p>
              We are thankful to {sponsor.name} for their partnership with
              Habitat for Humanity Buffalo in addressing the affordable housing
              crisis in Western New York since {sponsor.partnershipStartYear}.
            </p>
            <p>{sponsor.gratitudeFromHabitat}</p>

            {/* Gallery image between sections */}
            {sponsor.galleryImages && sponsor.galleryImages.length > 2 && (
              <LazyLoad height={150} offset={100}>
                <GalleryImage src={sponsor.galleryImages[2]} alt="Gallery" />
              </LazyLoad>
            )}
          </>
        )}

        {/* Sponsored Builds */}
        {sponsor.sponsoredBuilds && sponsor.sponsoredBuilds.length > 0 && (
          <>
            {sponsor.sponsoredBuilds.some((build) => !build.completionDate) && (
              <>
                <SectionTitle>Current Sponsored Builds</SectionTitle>
                <CurrentBuildsList>
                  {sponsor.sponsoredBuilds
                    .filter((build) => !build.completionDate)
                    .map((build) => (
                      <CurrentBuildCard key={build._id}>
                        <Link to={`/how-to-apply/project/${build._id}`}>
                          {build.mainImageUrl && (
                            <LazyLoad height={200} offset={100}>
                              <BuildImage
                                src={build.mainImageUrl}
                                alt={build.title}
                              />
                            </LazyLoad>
                          )}
                          <h3>{build.title}</h3>
                          <p>
                            <strong>Address:</strong> {build.address}
                          </p>
                          {build.description && (
                            <p>
                              <strong>Description:</strong> {build.description}
                            </p>
                          )}
                          <p>
                            <strong>Start Date:</strong>{" "}
                            {new Date(build.startDate).toLocaleDateString()}
                          </p>
                        </Link>
                      </CurrentBuildCard>
                    ))}
                </CurrentBuildsList>
              </>
            )}

            {sponsor.sponsoredBuilds.some((build) => build.completionDate) && (
              <>
                <h2>Previous Sponsored Builds</h2>
                <PreviousBuildsList>
                  {sponsor.sponsoredBuilds
                    .filter((build) => build.completionDate)
                    .map((build) => (
                      <PreviousBuildCard key={build._id}>
                        <Link to={`/builds/${build._id}`}>
                          {build.mainImageUrl && (
                            <LazyLoad height={150} offset={100}>
                              <BuildImage
                                src={build.mainImageUrl}
                                alt={build.title}
                              />
                            </LazyLoad>
                          )}
                          <h3>{build.title}</h3>
                          <p>{build.address}</p>
                        </Link>
                      </PreviousBuildCard>
                    ))}
                </PreviousBuildsList>
              </>
            )}
          </>
        )}

        {/* Gallery */}
        {sponsor.galleryImages && sponsor.galleryImages.length > 0 && (
          <>
            <SectionTitle>Gallery</SectionTitle>
            <GalleryContainer>
              {sponsor.galleryImages.map((imageUrl, index) => (
                <LazyLoad key={index} height={150} offset={100}>
                  <img src={imageUrl} alt={`Gallery Image ${index + 1}`} />
                </LazyLoad>
              ))}
            </GalleryContainer>
          </>
        )}

        {/* Related Videos */}
        {sponsor.videos && sponsor.videos.length > 0 && (
          <>
            <SectionTitle>Related Videos</SectionTitle>
            <VideoSection>
              {sponsor.videos.map((video, index) => (
                <VideoContainer key={index}>
                  <iframe
                    src={video.url}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                  <VideoTitle>{video.title}</VideoTitle>
                  <VideoDescription>{video.description}</VideoDescription>
                </VideoContainer>
              ))}
            </VideoSection>
          </>
        )}
      </SponsorDetailContainer>

      <CTASection
        title="Become a Sponsor"
        description="Join our mission and become a sponsor organization to help us fight the affordable housing crisis in Buffalo."
        buttonText="Learn More"
        buttonLink="/get-involved/sponsorship-packages"
      />
    </>
  );
};

// Styled Components

const HeaderSection = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  overflow: hidden;
  color: white;
`;

const HeaderBackgroundImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6); /* Darkens the image slightly */
`;

const HeaderContent = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 20px;
  color: var(--hhb-blue); /* Habitat Blue */
  text-align: center;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0),
    white
  ); /* Fade to white */
`;

const GalleryImage = styled.img`
  width: 100%;
  height: auto;
  margin: 20px 0;
  border-radius: 8px;
`;

const SectionTitle = styled.h2`
  font-size: 2em;
  margin: 20px 0;
  color: var(--hhb-primary);
`;

const SponsorDetailContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const DynamicOpeningStatement = styled.div`
  font-size: 1.5em;
  text-align: center;
`;

const SponsorLogo = styled.img`
  max-width: 350px;
  border-radius: 8px;
  margin: 0 auto 20px;
  display: block;
`;

const SponsorTitle = styled.h1`
  font-size: 2.5em;
  margin-bottom: 20px;
  text-align: center;
`;

const SponsorMeta = styled.div`
  text-align: center;
  margin-bottom: 20px;
  font-size: 1em;
`;

const CurrentBuildsList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 20px;
`;

const CurrentBuildCard = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  h3 {
    margin-top: 10px;
    font-size: 1.5em;
  }

  p {
    margin-top: 5px;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
`;

const PreviousBuildsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const PreviousBuildCard = styled.div`
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1.2em;
    margin-bottom: 5px;
  }

  p {
    font-size: 0.9em;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
`;

const BuildImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const GalleryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    object-fit: cover;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 1.5em;
  margin-top: 20px;
`;

const VideoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const VideoContainer = styled.div`
  iframe {
    width: 100%;
    height: 400px;
    border-radius: 8px;
  }
`;

const VideoTitle = styled.h3`
  font-size: 1.2em;
  margin-top: 10px;
`;

const VideoDescription = styled.p`
  font-size: 1em;
  margin-top: 5px;
  line-height: 1.5;
`;

export default SponsorDetail;
